import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Loader2, ImageIcon, ExternalLink, X as XIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const SUGGESTED = ["prayer", "scripture", "worship", "cross", "bible", "sunrise", "church", "faith"];

/**
 * Reusable Unsplash search picker.
 * Renders as a small "Browse Unsplash" link/button next to any image-URL input.
 *
 * Props:
 *   onSelect(url, photo) — called with the chosen direct image URL (regular size)
 *   sizePreference — "small" | "regular" | "thumb"   (default: "regular")
 *   trigger        — optional custom trigger element
 */
export function UnsplashPicker({ onSelect, sizePreference = "regular", trigger }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [committedQuery, setCommittedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [results, setResults] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const runSearch = useCallback(async (q, p) => {
    if (!q.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/unsplash/search", {
        params: { query: q.trim(), page: p, per_page: 12, orientation: "landscape" },
      });
      setResults(data.results || []);
      setTotalPages(data.total_pages || 0);
    } catch (err) {
      setError(err.userMessage || "Search failed");
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run a fresh search whenever the committed query or page changes
  useEffect(() => {
    if (committedQuery) runSearch(committedQuery, page);
  }, [committedQuery, page, runSearch]);

  const submit = (e) => {
    e.preventDefault();
    setPage(1);
    setCommittedQuery(query);
  };

  const pick = (photo) => {
    const url =
      photo[`url_${sizePreference}`] || photo.url_regular || photo.url_small || photo.url_thumb;
    if (!url) {
      toast.error("This photo has no usable URL — try another");
      return;
    }
    onSelect(url, photo);
    setOpen(false);
    toast.success(`Image set from Unsplash (by ${photo.user_name})`);
  };

  // Reset state when the dialog closes
  useEffect(() => {
    if (!open) {
      setQuery("");
      setCommittedQuery("");
      setPage(1);
      setResults([]);
      setTotalPages(0);
      setError("");
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="open-unsplash-picker"
          >
            <ImageIcon className="size-3.5 mr-1.5" /> Browse Unsplash
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="size-5" /> Browse Unsplash
          </DialogTitle>
          <DialogDescription>
            Search free, high-quality photos. Photographers are credited automatically when an image
            is selected.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <Input
              data-testid="unsplash-query-input"
              placeholder="Search e.g. prayer, scripture, sunrise…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>
          <Button type="submit" disabled={loading || !query.trim()} data-testid="unsplash-search-btn">
            {loading ? <Loader2 className="size-4 animate-spin" /> : "Search"}
          </Button>
        </form>

        {!committedQuery && !loading && (
          <div className="text-xs text-muted-foreground">
            Try:{" "}
            {SUGGESTED.map((kw) => (
              <button
                key={kw}
                type="button"
                onClick={() => {
                  setQuery(kw);
                  setPage(1);
                  setCommittedQuery(kw);
                }}
                className="inline-block mr-1.5 mb-1 px-2 py-0.5 rounded-full border border-border bg-background hover:bg-muted transition-colors"
                data-testid={`unsplash-chip-${kw}`}
              >
                {kw}
              </button>
            ))}
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">{error}</p>
        )}

        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[4/3] rounded-md bg-muted animate-pulse" />
              ))}
            </div>
          ) : results.length === 0 && committedQuery ? (
            <div className="py-10 text-center">
              <ImageIcon className="size-8 mx-auto text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                No results for "<span className="font-medium">{committedQuery}</span>". Try a different keyword.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {results.map((photo) => (
                <button
                  type="button"
                  key={photo.id}
                  onClick={() => pick(photo)}
                  data-testid={`unsplash-photo-${photo.id}`}
                  className="group relative aspect-[4/3] rounded-md overflow-hidden bg-muted border border-border hover:ring-2 hover:ring-primary transition-all"
                  style={{ backgroundColor: photo.color || undefined }}
                >
                  <img
                    src={photo.url_thumb}
                    alt={photo.description || `Photo by ${photo.user_name}`}
                    loading="lazy"
                    className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                  />
                  {/* Photographer overlay */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 text-left">
                    <p className="text-[11px] text-white/90 truncate">
                      {photo.user_name || "Unsplash"}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {results.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border pt-3 shrink-0">
            <span className="text-xs text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="unsplash-prev-page"
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                data-testid="unsplash-next-page"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        <p className="text-[10px] text-muted-foreground text-center shrink-0">
          Photos via{" "}
          <a
            href="https://unsplash.com/?utm_source=greater_life_admin&utm_medium=referral"
            target="_blank"
            rel="noreferrer"
            className="underline inline-flex items-center gap-0.5"
          >
            Unsplash <ExternalLink className="size-2.5" />
          </a>
          . Free to use under the Unsplash License.
        </p>
      </DialogContent>
    </Dialog>
  );
}
